import type { ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelectedPharmacy } from 'app/hooks';
import { PlanFeaturesErrorState } from 'app/shared-components/PlusPaywall';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

import { ONBOARDING_PATHS } from './constants/paths';
import { OnboardingLayout } from './components/OnboardingLayout';
import { useIsBespoke } from '../../hooks/useWebBuilderAccess';
import { useOnboarding } from './store/onboarding-store';
import { useCreatePlusWebsite } from './hooks/useCreatePlusWebsite';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';

type DomainOption = 'have' | 'none';

const DomainStep = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const {
    hasDomain,
    domain,
    primaryColor,
    logoUrl,
    landingType,
    externalHomepageUrl,
    setHasDomain,
    setDomain,
    setWebsiteCreated,
  } = useOnboarding();
  const { enqueueSnackbar } = useSnackbar();
  const pharmacy = useSelectedPharmacy();
  const { isBespoke, isError: isBespokeError, retry: retryBespoke } = useIsBespoke();
  const { mutate: createWebsite, isLoading } = useCreatePlusWebsite();
  const { trackDomainSaved } = useOnboardingTracker();
  const selected: DomainOption = hasDomain ? 'have' : 'none';

  if (isBespokeError) return <PlanFeaturesErrorState onRetry={retryBespoke} />;

  // Bespoke orgs skip homepage, so back should reach past it to logo.
  const backTo = isBespoke ? ONBOARDING_PATHS.logo : ONBOARDING_PATHS.homepage;

  const handleSubmit = () => {
    const normalisedDomain = domain.trim();
    if (hasDomain && !normalisedDomain) {
      enqueueSnackbar('Please enter your domain', { variant: 'error' });
      return;
    }
    trackDomainSaved();
    createWebsite(
      {
        organisationId,
        primaryColor,
        logoUrl: logoUrl || undefined,
        landingType,
        externalHomepageUrl: landingType === 'external' ? externalHomepageUrl : undefined,
        domain: normalisedDomain || undefined,
      },
      {
        onSuccess: () => {
          setWebsiteCreated();
          navigate({
            pathname: ONBOARDING_PATHS.success,
            search: searchParams.toString(),
          });
        },
      }
    );
  };

  return (
    <OnboardingLayout
      activeStep={4}
      backTo={backTo}
      primaryLabel={isLoading ? 'Creating…' : 'Continue'}
      primaryDisabled={isLoading}
      onPrimary={handleSubmit}
    >
      <div className="vtwo:text-center vtwo:mb-10">
        <h1 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-3">Set up your domain</h1>
        <p className="vtwo:text-base vtwo:text-gray-600">
          The web address where your site will live
        </p>
      </div>

      <RadioGroup
        value={selected}
        onValueChange={(val) => setHasDomain(val === 'have')}
        className="vtwo:max-w-3xl vtwo:mx-auto vtwo:gap-4"
      >
        <div
          className={cn(
            'vtwo:rounded-xl vtwo:border vtwo:p-6 vtwo:transition-colors',
            selected === 'have' ? 'vtwo:border-featured' : 'vtwo:border-gray-200'
          )}
        >
          <Label
            htmlFor="option-have"
            className="vtwo:flex vtwo:items-start vtwo:gap-3 vtwo:cursor-pointer vtwo:font-normal"
          >
            <RadioGroupItem value="have" id="option-have" className="vtwo:mt-0.5" />
            <p className="vtwo:text-sm vtwo:font-bold">I have a domain</p>
          </Label>
          {selected === 'have' && (
            <div className="vtwo:mt-4 vtwo:ml-7">
              <Input
                placeholder="e.g. mysite.com"
                className="vtwo:mb-2"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
              <p className="vtwo:text-xs vtwo:text-gray-400">Enter the domain you already own</p>
            </div>
          )}
        </div>

        <div
          className={cn(
            'vtwo:rounded-xl vtwo:border vtwo:p-6 vtwo:transition-colors',
            selected === 'none' ? 'vtwo:border-featured' : 'vtwo:border-gray-200'
          )}
        >
          <Label
            htmlFor="option-none"
            className="vtwo:flex vtwo:items-start vtwo:gap-3 vtwo:cursor-pointer vtwo:font-normal"
          >
            <RadioGroupItem value="none" id="option-none" className="vtwo:mt-0.5" />
            <p className="vtwo:text-sm vtwo:font-bold">I don't have domain yet</p>
          </Label>
          {selected === 'none' && (
            <div className="vtwo:mt-4 vtwo:ml-7">
              <Input
                placeholder="Preferred domain URL (optional)"
                className="vtwo:mb-2"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
              <p className="vtwo:text-xs vtwo:text-gray-400">
                Don&apos;t have one yet?{' '}
                <a
                  href={`https://www.godaddy.com/en-uk/domainsearch/find?isc=GDWELCOME&tmskey=nc_domain_offer&domainToCheck=${encodeURIComponent(
                    pharmacy?.pharmacy_name ?? ''
                  )}&checkAvail=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vtwo:underline"
                >
                  Search for one
                </a>
              </p>
            </div>
          )}
        </div>
      </RadioGroup>
    </OnboardingLayout>
  );
};

export default DomainStep;
